import { useEffect } from "react";
import {
  sendEmailVerification,
  signInWithEmailAndPassword,
  signInWithPopup,
} from "firebase/auth";
import {
  ActionFunctionArgs,
  isRouteErrorResponse,
  json,
  LoaderFunctionArgs,
  redirect,
  useRouteError,
} from "react-router-dom";
import Result from "antd/es/result";
import { Button, message } from "antd";
import { auth, provider } from "~/lib";
import { INTENTS, getSafeReturnUrl } from "~/utils";
import {
  addFirebaseTenant,
  ensureTenantExists,
} from "~/services/server/user-tenant";
import { SignUpFormValues, SignInFormValues } from "~/types";
import { getShadowInviteResumeUrl } from "~/utils/shadowInviteStorage";

const SIGNUP_LOG_PREFIX = "[SignUpPage]";

/** Prefer pending shadow-invite URL when returnUrl is missing or defaulted to home. */
function resolvePostAuthDestination(safeReturnUrl: string): string {
  if (safeReturnUrl && safeReturnUrl !== "/") {
    return safeReturnUrl;
  }
  return getShadowInviteResumeUrl() ?? "/";
}

function serializeUnknownForLog(value: unknown): Record<string, unknown> {
  if (value instanceof Error) {
    const withCode = value as Error & { code?: string };
    return {
      name: value.name,
      message: value.message,
      code: withCode.code,
      stack: value.stack,
    };
  }
  if (value && typeof value === "object") {
    try {
      return JSON.parse(JSON.stringify(value)) as Record<string, unknown>;
    } catch {
      return { fallback: String(value) };
    }
  }
  return { value: String(value) };
}

function logSignupError(phase: string, details: Record<string, unknown>) {
  console.error(SIGNUP_LOG_PREFIX, phase, details);
}

function toUserFacingErrorMessage(error: unknown): string {
  if (typeof error === "string") return error;
  if (error instanceof Error) return error.message;
  if (error && typeof error === "object") {
    const o = error as Record<string, unknown>;
    if (typeof o.message === "string") return o.message;
    if (typeof o.error === "string") return o.error;
  }
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

/** HTTP 200 so useFetcher receives data and the UI can message.error instead of the route error boundary. */
function userFacingActionError(
  isSignUp: boolean,
  error: unknown,
  code?: string
) {
  const msg = toUserFacingErrorMessage(error);
  return json(
    { error: msg, isSignUp, ...(code ? { code } : {}) },
    { status: 200 }
  );
}

export const SignUpPageLoader = async ({ request }: LoaderFunctionArgs) => {
  const user = auth.currentUser;

  // Read returnUrl from URL search params
  const { searchParams } = new URL(request.url);
  const returnUrl = searchParams.get("returnUrl");
  const safeReturnUrl = getSafeReturnUrl(returnUrl);

  // If user is already authenticated, redirect to returnUrl (or "/" if no returnUrl)
  if (user) {
    return redirect(resolvePostAuthDestination(safeReturnUrl));
  }

  // If not authenticated, allow access to signup page and pass returnUrl
  return { success: true, returnUrl: safeReturnUrl };
};

export type SignUpPageLoaderType = {
  success: boolean;
  returnUrl: string;
};

export const SignUpPageAction = async ({ request }: ActionFunctionArgs) => {
  const formData = await request.formData();
  const intent = formData.get("intent");

  // Read returnUrl from form data or URL search params
  const returnUrlFromForm = formData.get("returnUrl") as string | null;
  const { searchParams } = new URL(request.url);
  const returnUrlFromQuery = searchParams.get("returnUrl");
  const returnUrl = returnUrlFromForm || returnUrlFromQuery;
  const safeReturnUrl = getSafeReturnUrl(returnUrl);

  switch (intent) {
    case INTENTS._signup: {
      try {
        let payload: SignUpFormValues;
        try {
          payload = JSON.parse(formData.get("body") as string) as SignUpFormValues;
        } catch (parseErr) {
          logSignupError("email_signup_parse_body", {
            error: serializeUnknownForLog(parseErr),
          });
          return userFacingActionError(true, "Invalid signup form data");
        }

        const newTenant = await addFirebaseTenant(payload, true);
        if (!newTenant.success) {
          logSignupError("email_signup_add_firebase_tenant", {
            error: newTenant.error,
            email: payload.email,
          });
          return userFacingActionError(true, newTenant.error);
        }

        // User is now signed in; ensure DynamoDB tenant via Lambda (same flow as property-mgmt ensureUserExists)
        const currentUser = auth.currentUser;
        if (!currentUser) {
          logSignupError("email_signup_no_current_user_after_create", {
            email: payload.email,
          });
          return userFacingActionError(
            true,
            "Failed to get auth state after sign up"
          );
        }
        const idToken = await currentUser.getIdToken();

        const ensureResult = await ensureTenantExists(
          {
            id: currentUser.uid,
            email: payload.email,
            username: payload.username,
            firstName: payload.firstName,
            lastName: payload.lastName,
          },
          { idToken }
        );
        if (!ensureResult.success) {
          logSignupError("email_signup_ensure_tenant_failed", {
            error: serializeUnknownForLog(ensureResult.error),
            uid: currentUser.uid,
          });
          return userFacingActionError(true, ensureResult.error);
        }

        return redirect(resolvePostAuthDestination(safeReturnUrl));
      } catch (err) {
        const fb = err as { code?: string; message?: string };
        logSignupError("email_signup_unhandled_throw", {
          error: serializeUnknownForLog(err),
        });
        return userFacingActionError(
          true,
          fb?.message ?? err,
          fb?.code
        );
      }
    }
    case INTENTS._signin: {
      try {
        let payload: SignInFormValues;
        try {
          payload = JSON.parse(
            formData.get("body") as string
          ) as SignInFormValues;
        } catch {
          return userFacingActionError(false, "Invalid sign-in form data");
        }
        await signInWithEmailAndPassword(auth, payload.email, payload.password);

        const user = auth.currentUser;
        if (!user) {
          return userFacingActionError(
            false,
            "Failed to get auth state after sign in"
          );
        }
        const idToken = await user.getIdToken();
        const [firstName, ...lastNameParts] = (user.displayName || "").split(
          " "
        );
        const lastName = lastNameParts.join(" ");

        const ensureResult = await ensureTenantExists(
          {
            id: user.uid,
            email: user.email || payload.email,
            username: user.email || payload.email,
            firstName: firstName || "",
            lastName: lastName || "",
          },
          { idToken }
        );
        if (!ensureResult.success) {
          return userFacingActionError(false, ensureResult.error);
        }

        message.success("Login successful!"); // TODO [Translate]
        return redirect(resolvePostAuthDestination(safeReturnUrl));
      } catch (error) {
        logSignupError("email_signin_error", {
          error: serializeUnknownForLog(error),
        });
        const fb = error as { code?: string; message?: string };
        return userFacingActionError(
          false,
          fb?.message ?? error,
          fb?.code
        );
      }
    }
    case INTENTS._google_signin: {
      try {
        // Sign in with Google (will sign in if account exists)
        const userCredential = await signInWithPopup(auth, provider);
        if (!userCredential) {
          console.error(SIGNUP_LOG_PREFIX, "google_signin_no_credential");
          message.error("Error signing in with Google");
          return redirect(resolvePostAuthDestination(safeReturnUrl));
        }

        const user = userCredential.user;
        const [firstName, ...lastNameParts] = (user.displayName || "").split(
          " "
        );
        const lastName = lastNameParts.join(" ");
        const idToken = await user.getIdToken();

        // Ensure tenant exists in Firestore (here) and DynamoDB (Lambda)
        const ensureResult = await ensureTenantExists(
          {
            id: user.uid,
            email: user.email || "",
            username: user.email || "",
            firstName: firstName || "",
            lastName: lastName || "",
          },
          { idToken }
        );

        if (!ensureResult.success) {
          logSignupError("google_signin_ensure_tenant_failed", {
            error: serializeUnknownForLog(ensureResult.error),
          });
          return userFacingActionError(false, ensureResult.error);
        }

        message.success("Login successful!"); // TODO [Translate]
        return redirect(resolvePostAuthDestination(safeReturnUrl));
      } catch (error) {
        logSignupError("google_signin_error", {
          error: serializeUnknownForLog(error),
        });
        return userFacingActionError(false, error);
      }
    }
    case INTENTS._google_signup: {
      try {
        // For sign-up, signInWithPopup will create the Firebase account if it doesn't exist
        // If the account already exists, it will just sign in
        const userCredential = await signInWithPopup(auth, provider);
        if (!userCredential) {
          console.error(SIGNUP_LOG_PREFIX, "google_signup_no_credential");
          message.error("Error signing up with Google");
          return redirect(resolvePostAuthDestination(safeReturnUrl));
        }

        const user = userCredential.user;
        const [firstName, ...lastNameParts] = (user.displayName || "").split(
          " "
        );
        const lastName = lastNameParts.join(" ");
        const idToken = await user.getIdToken();

        // Ensure tenant exists in Firestore (here) and DynamoDB (Lambda)
        const ensureResult = await ensureTenantExists(
          {
            id: user.uid,
            email: user.email || "",
            username: user.email || "",
            firstName: firstName || "",
            lastName: lastName || "",
          },
          { idToken }
        );

        if (!ensureResult.success) {
          logSignupError("google_signup_ensure_tenant_failed", {
            error: serializeUnknownForLog(ensureResult.error),
          });
          return userFacingActionError(true, ensureResult.error);
        }

        // Send email verification for new sign-ups
        if (auth.currentUser) {
          await sendEmailVerification(auth.currentUser);
        }

        return redirect(resolvePostAuthDestination(safeReturnUrl));
      } catch (error) {
        logSignupError("google_signup_error", {
          error: serializeUnknownForLog(error),
        });
        return userFacingActionError(true, error);
      }
    }
  }
  return {};
};

export const SignUpPageErrorBoundary = () => {
  const error = useRouteError();
  let subTitle = "Sorry, something went wrong."; // TODO [Translate]

  useEffect(() => {
    if (isRouteErrorResponse(error)) {
      console.error(SIGNUP_LOG_PREFIX, "error_boundary_route", {
        status: error.status,
        statusText: error.statusText,
        data: error.data,
      });
      return;
    }
    console.error(SIGNUP_LOG_PREFIX, "error_boundary", {
      error: serializeUnknownForLog(error),
    });
  }, [error]);

  if (isRouteErrorResponse(error)) {
    subTitle = `Sorry, something went wrong during ${
      (error.data as { isSignUp?: boolean } | undefined)?.isSignUp
        ? "sign up"
        : "sign in"
    }.`; // TODO [Translate]
  }

  return (
    <Result status="500" title="500" subTitle={subTitle}>
      <div style={{ display: "flex", justifyContent: "center" }}>
        <Button type="primary" href="/">
          Back Home
        </Button>
      </div>
    </Result>
  );
};
